import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import usePageMeta from '../../../hooks/usePageMeta';

export default function ManagementPage() {
  usePageMeta({
    title: '미래인재학원 확실한 관리 - 체계적 성적',
    description: '미래인재학원의 체계적 관리 시스템! 정기 테스트, 매일 카톡 학습 보고, 성적 분석 및 밀착 상담을 통해 학생별 최상의 입시 결과를 만들어갑니다.',
    path: '/about/management',
    schemaType: 'WebPage',
    keywords: '미래인재학원 관리, 출결관리, 카톡보고서, 영상보충, 학습관리시스템',
  });

  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slideImages = [
    {
      src: "https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/70554ab005f7cb5f259ec6a0eca90479.png",
      alt: "영상 보충 시스템 1"
    },
    {
      src: "https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/95939b5934f84a5fd64ac0ab2b3e35d8.png",
      alt: "영상 보충 시스템 2"
    }
  ];

  // 5초 간격 자동 슬라이드
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [slideImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slideImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slideImages.length) % slideImages.length);
  };

  return (
    <div className="min-h-screen bg-neutral-950">
      <Navigation currentPage="about" />

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-4 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.15)_0%,transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(251,191,36,0.08)_0%,transparent_40%)]"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-400/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-10 w-96 h-96 bg-yellow-400/5 rounded-full blur-3xl"></div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full mb-8">
            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
            <span className="text-yellow-400 text-sm font-semibold tracking-wider">MANAGEMENT</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6 tracking-tight">
            <span className="md:hidden">
              미래인재학원<br />
              <span className="text-yellow-400">확실한 관리</span>
            </span>
            <span className="hidden md:inline">
              미래인재학원 확실한 <span className="text-yellow-400">관리</span>
            </span>
          </h1>
          <p className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto leading-relaxed">
            철저한 출결 관리, 숙제 카톡 보고, 영상 보충 시스템으로<br className="hidden md:block" />
            빈틈없는 학습 관리를 제공합니다
          </p>
        </div>
      </section>

      {/* Section 1: 철저한 출결 관리 */}
      <section className="py-24 px-4 bg-neutral-900 relative">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent"></div>

        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="px-4 py-1.5 bg-yellow-400 rounded-md">
              <span className="text-black text-xs font-black tracking-wider">STEP 01</span>
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-yellow-400/50 to-transparent max-w-[200px]"></div>
          </div>
          <div className="text-3xl md:text-4xl font-black text-white leading-tight mb-12">
            철저한 <span className="text-yellow-400">출결 관리</span>
          </div>

          {/* Flow Chart - 무통보 지각/결석 */}
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-white font-bold text-lg">무통보 지각/결석</span>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6">
              {/* Step 1 */}
              <div className="group flex-1 w-full">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/80 rounded-2xl p-6 text-center group-hover:border-yellow-400/50 transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-500/25">
                    <i className="ri-alarm-warning-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-white font-bold mb-2">수업 무통보 지각/결석</h4>
                  <p className="text-neutral-400 text-sm">사전 연락 없이 결석 또는 지각</p>
                </div>
              </div>

              {/* Arrow */}
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-400/30 flex-shrink-0 rotate-90 lg:rotate-0">
                <i className="ri-arrow-right-line text-xl text-black"></i>
              </div>

              {/* Step 2 */}
              <div className="group flex-1 w-full">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/80 rounded-2xl p-6 text-center group-hover:border-yellow-400/50 transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-400/25">
                    <i className="ri-timer-flash-line text-2xl md:text-3xl text-black"></i>
                  </div>
                  <h4 className="text-white font-bold mb-2">카운터 5분 안에 보고</h4>
                  <p className="text-neutral-400 text-sm">담당 선생님이 즉시 보고</p>
                </div>
              </div>

              {/* Arrow */}
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-400/30 flex-shrink-0 rotate-90 lg:rotate-0">
                <i className="ri-arrow-right-line text-xl text-black"></i>
              </div>

              {/* Step 3 */}
              <div className="group flex-1 w-full">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/80 rounded-2xl p-6 text-center group-hover:border-yellow-400/50 transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/25">
                    <i className="ri-phone-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-white font-bold mb-2">행정 실장님이 부모님께 보고</h4>
                  <p className="text-neutral-400 text-sm">즉시 학부모님께 연락</p>
                </div>
              </div>
            </div>
          </div>

          {/* Flow Chart - 통보 지각/결석 */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-white font-bold text-lg">통보 지각/결석 (개인 사정)</span>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-4 lg:gap-6">
              {/* Step 1 */}
              <div className="group flex-1 w-full">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/80 rounded-2xl p-6 text-center group-hover:border-yellow-400/50 transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/25">
                    <i className="ri-message-2-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-white font-bold mb-2">통보 지각/결석</h4>
                  <p className="text-neutral-400 text-sm">개인 사정으로 사전 연락</p>
                </div>
              </div>

              {/* Arrow */}
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-400/30 flex-shrink-0 rotate-90 lg:rotate-0">
                <i className="ri-arrow-right-line text-xl text-black"></i>
              </div>

              {/* Step 2 */}
              <div className="group flex-1 w-full">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/80 rounded-2xl p-6 text-center group-hover:border-yellow-400/50 transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-400/25">
                    <i className="ri-file-list-3-line text-2xl md:text-3xl text-black"></i>
                  </div>
                  <h4 className="text-white font-bold mb-2">카운터에 보고</h4>
                  <p className="text-neutral-400 text-sm">담당 선생님이 즉시 보고</p>
                </div>
              </div>

              {/* Arrow */}
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center shadow-lg shadow-yellow-400/30 flex-shrink-0 rotate-90 lg:rotate-0">
                <i className="ri-arrow-right-line text-xl text-black"></i>
              </div>

              {/* Step 3 */}
              <div className="group flex-1 w-full">
                <div className="bg-neutral-800/80 backdrop-blur-sm border border-neutral-700/80 rounded-2xl p-6 text-center group-hover:border-yellow-400/50 transition-all duration-300">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/25">
                    <i className="ri-checkbox-circle-line text-2xl md:text-3xl text-white"></i>
                  </div>
                  <h4 className="text-white font-bold mb-2">행정 실장님이 결석 사유 확인</h4>
                  <p className="text-neutral-400 text-sm">학부모님께 사유 확인 연락</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: 카톡 보고서 */}
      <section className="py-24 px-4 bg-yellow-400 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-300 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-yellow-500 rounded-full translate-y-1/2 -translate-x-1/2 opacity-30"></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="px-4 py-1.5 bg-black rounded-md">
                  <span className="text-yellow-400 text-xs font-black tracking-wider">STEP 02</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-black/30 to-transparent max-w-[200px]"></div>
              </div>
              <div className="text-3xl md:text-4xl font-black text-black leading-tight mb-8">
                카톡 <span className="text-neutral-800">보고서</span>
              </div>

              <div className="space-y-5">
                {/* 영어 */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-black/10 shadow-lg shadow-black/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <i className="ri-english-input text-xl text-yellow-400"></i>
                    </div>
                    <div>
                      <h4 className="text-black font-bold text-lg">영어</h4>
                      <p className="text-black/70 text-sm">숙제 완수율</p>
                    </div>
                  </div>
                </div>

                {/* 수학 */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-black/10 shadow-lg shadow-black/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <i className="ri-calculator-line text-xl text-yellow-400"></i>
                    </div>
                    <div>
                      <h4 className="text-black font-bold text-lg">수학</h4>
                      <p className="text-black/70 text-sm">숙제 완수율, 일일테스트 점수</p>
                    </div>
                  </div>
                </div>

                {/* 과학 */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-black/10 shadow-lg shadow-black/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <i className="ri-flask-line text-xl text-yellow-400"></i>
                    </div>
                    <div>
                      <h4 className="text-black font-bold text-lg">과학</h4>
                      <p className="text-black/70 text-sm">숙제 완수율</p>
                    </div>
                  </div>
                </div>

                {/* 국어 */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 border border-black/10 shadow-lg shadow-black/10">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl flex items-center justify-center shadow-md flex-shrink-0">
                      <i className="ri-book-2-line text-xl text-yellow-400"></i>
                    </div>
                    <div>
                      <h4 className="text-black font-bold text-lg">국어</h4>
                      <p className="text-black/70 text-sm">주간 테스트</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Image Placeholder */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-black/10 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-black/10 shadow-xl shadow-black/10 overflow-hidden aspect-[4/5]">
                <img
                  src="https://static.readdy.ai/image/2e28ff317bf3b761880ac8d7949ab85c/20ba74c2b77a29e25bc3f622bf04091c.png"
                  alt="카톡 보고서 예시"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: 영상 보충 시스템 */}
      <section className="py-24 px-4 bg-neutral-900 relative">
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent"></div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="px-4 py-1.5 bg-yellow-400 rounded-md">
                  <span className="text-black text-xs font-black tracking-wider">STEP 03</span>
                </div>
                <div className="h-px flex-1 bg-gradient-to-r from-yellow-400/50 to-transparent max-w-[200px]"></div>
              </div>
              <div className="text-3xl md:text-4xl font-black text-white leading-tight mb-8">
                영상 <span className="text-yellow-400">보충 시스템</span>
              </div>

              <div className="text-lg md:text-xl text-neutral-300 leading-[1.8] mb-8">
                <p>
                  학원에서 진행하는 모든 수업은
                  <br className="md:hidden" />
                  <span className="text-yellow-400 font-bold"> 실시간으로 영상 촬영</span>이 되고 있습니다.
                  <br />
                  결석 시에는 학생들이&nbsp;<span className="text-yellow-400 font-bold">선생님의 지도하에</span>
                  <br />
                  보충 영상을 다시 수강하는 것을 원칙으로 하고 있습니다.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-4 bg-neutral-800/80 rounded-xl border border-neutral-700/80">
                  <div className="w-10 h-10 bg-yellow-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-video-line text-yellow-400 text-xl"></i>
                  </div>
                  <span className="text-neutral-300">모든 수업 실시간 녹화</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-neutral-800/80 rounded-xl border border-neutral-700/80">
                  <div className="w-10 h-10 bg-yellow-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-follow-line text-yellow-400 text-xl"></i>
                  </div>
                  <span className="text-neutral-300">선생님 지도하에 보충 수강</span>
                </div>
                <div className="flex items-center gap-3 p-4 bg-neutral-800/80 rounded-xl border border-neutral-700/80">
                  <div className="w-10 h-10 bg-yellow-400/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-line text-yellow-400 text-xl"></i>
                  </div>
                  <span className="text-neutral-300">결석해도 수업 내용 100% 보충</span>
                </div>
              </div>
            </div>

            {/* Right - Image Slider */}
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 to-transparent rounded-3xl blur-2xl"></div>
              <div className="relative bg-neutral-800/80 backdrop-blur-sm rounded-2xl border border-neutral-700/80 shadow-xl overflow-hidden aspect-[4/3]">
                {/* Stacked Images */}
                <div className="relative w-full h-full">
                  {slideImages.map((image, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-500 ease-in-out ${
                        index === currentSlide 
                          ? 'opacity-100 translate-x-0 z-10' 
                          : index < currentSlide 
                            ? 'opacity-0 -translate-x-full z-0' 
                            : 'opacity-0 translate-x-full z-0'
                      }`}
                    >
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
                </div>

                {/* Navigation Buttons */}
                <button
                  onClick={prevSlide}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-white text-xl"></i>
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-white text-xl"></i>
                </button>

                {/* Slide Indicators */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
                  {slideImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                        index === currentSlide 
                          ? 'bg-yellow-400 w-6' 
                          : 'bg-white/50 hover:bg-white/80'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-black relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.1)_0%,transparent_50%)]"></div>
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-yellow-400/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-yellow-400/5 rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-400/10 border border-yellow-400/30 rounded-full mb-8">
            <i className="ri-shield-check-line text-yellow-400"></i>
            <span className="text-yellow-400 text-sm font-semibold">TRUST</span>
          </div>
          <div className="text-3xl md:text-5xl font-black text-white mb-6">
            <span className="text-yellow-400">빈틈없는 관리</span>로&nbsp;<br className="md:hidden" />
            신뢰를 드립니다
          </div>
          <p className="text-lg text-neutral-400 mb-10 max-w-xl mx-auto leading-relaxed">
            출결 관리부터 숙제 보고, 영상 보충까지
            <br />
            학부모님께서 안심하실 수 있는 관리 시스템
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-10 py-5 bg-yellow-400 text-black font-bold text-lg rounded-full hover:bg-yellow-300 hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-xl shadow-yellow-400/25"
          >
            <span>입학상담 신청하기</span>
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
